import { CdkScrollable } from '@angular/cdk/scrolling';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import structuredClone from '@ungap/structured-clone';
import { Subscription } from 'rxjs';
import { debounceTime, filter, finalize } from 'rxjs/operators';
import { WebSocketNotificationType, WebSocketTaskNotification } from 'src/app/core/websocket/model';
import { WebSocketService } from 'src/app/core/websocket/websocket.service';
import { BasicService } from 'src/app/shared/services/basic.service';
import { Task } from 'src/app/tasks/shared/models';

import { FiltersService } from '../filters/filters.service';
import { MassActionsService } from '../mass-actions/mass-actions.service';
import { CountersService } from '../services/counters.service';
import { NavigationService } from '../services/navigation.service';
import { TaskListService } from '../services/task-list.service';
import { QueueIdEnum, TaskListModeEnum } from '../shared/enums';
import { TaskList, TaskListConfiguration, TaskListReorder } from '../shared/models';

@Component({
  selector: 'app-task-list',
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TaskListComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() taskListConfiguration: TaskListConfiguration;
  @Input() taskListMode: TaskListModeEnum;
  @Output() tasksIdsLengthChange = new EventEmitter<number>();
  private stateInboxNg = 'auth.alcmeon.inbox.list';
  private stateInboxNgTasks = `${this.stateInboxNg}.tasks`;
  private statePushboxNg = 'auth.alcmeon.pushbox';
  private statePushboxNgTasks = `${this.statePushboxNg}.tasks`;
  private loadMoreTasksCount = 20;
  tasksIds: number[] = [];
  tasksById: { [key: number]: Task } = {};
  loadedTasksIds: number[] = [];
  isLoading: boolean;
  isLoadingMoreTasks = false;
  hasNewTasksIds = false;
  newTasksIds: Set<number> = new Set();
  redirectTextAndLink = '';
  private currentTitle: string;
  private titleBlinkInterval: any;
  showInsertsByMe = false;
  isFiltersSelected = false;
  taskListModeEnum = TaskListModeEnum;

  private subscriptions = new Subscription();

  @ViewChild(CdkScrollable, { static: false }) public taskList: CdkScrollable;

  constructor(
    private basicService: BasicService,
    private countersService: CountersService,
    private filtersService: FiltersService,
    private massActionsService: MassActionsService,
    private navigationService: NavigationService,
    private taskListService: TaskListService,
    private webSocketService: WebSocketService,
    @Inject(DOCUMENT) public document: Document,
    @Inject('$state') private $state: any,
    @Inject('$scope') private $scope: any
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this.massActionsService.toggleTasks(false);

    let updatedTaskListConfiguration: TaskListConfiguration = this.taskListConfiguration;

    if (this.taskListMode === TaskListModeEnum.Inbox) {
      this.showInsertsByMe = true;
      this.currentTitle = this.document.querySelector('title').innerText;
      this.isFiltersSelected = this.filtersService.isFiltersOrSitesSelected;

      if (this.taskListConfiguration.queueId === QueueIdEnum.AssignedToYou) {
        this.redirectTextAndLink = this.basicService.translate('inbox/task-list/no-result/go-to-received', {
          received: this.basicService.translate('inbox/tab/received'),
        });
      }

      const { parameters, sort_type_id, filters } = this.taskListConfiguration;
      updatedTaskListConfiguration = { parameters, sort_type_id, filters };

      // the goal is to hide <app-inserts-by-me> when in PP, so when coming back in inbox, it triggers its ngOnInit wich will allow to load counters.
      this.$scope.$watch(
        () => this.$state.includes('**.process'),
        () => {
          if (!this.$state.includes('**.process')) {
            this.showInsertsByMe = true;
          } else {
            this.showInsertsByMe = false;
          }
        }
      );
    }

    this.taskListService
      .getTasks(updatedTaskListConfiguration)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (result: TaskList) => {
          if (this.taskListMode === this.taskListModeEnum.Inbox) {
            this.filtersService.updateContextualFilters(result);
            this.filtersService.updateTplFiltersBS.next();
          }
          this.initTasksScopeData(result.tasks_ids, result.tasks_by_id);
        },
      });

    this.subscriptions.add(
      this.taskListService.reorderTasksBS.subscribe(() => {
        this.reorderTasks();
      })
    );

    // Task notification
    this.subscriptions.add(
      this.webSocketService.message$
        .pipe(filter((event: any) => event.type === WebSocketNotificationType.Task))
        .subscribe({
          next: (taskEvent: WebSocketTaskNotification) => {
            if (this.taskListMode === TaskListModeEnum.Inbox || this.taskListMode === TaskListModeEnum.Pushbox) {
              this.countersService.updateCounters(taskEvent);
              this.notifyNewTask(taskEvent);
            }

            // refresh one task (typically when navigating between inbox and PP, but not only)
            if (this.tasksById[taskEvent.task_id]) {
              this.lazyLoadTasks([taskEvent.task_id]);
            }
          },
        })
    );

    // CRM notification
    this.subscriptions.add(
      this.webSocketService.message$
        .pipe(filter((event: any) => event.type === WebSocketNotificationType.ExternalUserCrm))
        .subscribe({
          next: taskEvent => {
            this.loadedTasksIds.forEach(taskId => {
              if (taskEvent.user_id === this.tasksById[taskId].message.from.id) {
                this.tasksById[taskId].message.from.crm_organizations_links = taskEvent.crm_organizations_links;
                this.tasksById[taskId].message.from.is_vip = taskEvent.is_vip;
              }
            });
          },
        })
    );
  }

  ngAfterViewInit() {
    this.subscriptions.add(
      this.taskList
        .elementScrolled()
        .pipe(
          debounceTime(150),
          filter(() => {
            return this.taskList.measureScrollOffset('bottom') <= 920; // padding bottom 600px + 2*160px messages
          })
        )
        .subscribe(() => {
          this.loadMoreTasks();
        })
    );
  }

  initTasksScopeData(tasksIds: number[], tasksById: { [key: number]: Task }) {
    if (tasksIds !== undefined && tasksById !== undefined) {
      this.tasksIds = tasksIds;
      if (this.taskListMode === TaskListModeEnum.Search) {
        this.tasksIdsLengthChange.emit(tasksIds.length);
      }
      const tasksIdsCopy = structuredClone(this.tasksIds);
      this.tasksById = tasksById;
      this.loadedTasksIds = tasksIdsCopy.splice(0, Object.keys(this.tasksById).length);
    }
  }

  reorderTasks() {
    this.isLoading = true;

    const taskList: TaskListReorder = {
      tasks_ids: this.tasksIds,
      sort_type_id: this.taskListConfiguration.sort_type_id,
      states: this.taskListConfiguration.parameters.states,
      admin_users_ids: this.taskListConfiguration.parameters.admin_users_ids,
    };

    this.taskListService
      .reorderTasks(taskList)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (result: TaskList) => {
          this.initTasksScopeData(result.tasks_ids, result.tasks_by_id);
        },
      });
  }

  /**
   * On scroll
   */
  loadMoreTasks() {
    if (this.loadedTasksIds?.length === 0) {
      return;
    }

    const loadedTasksIdsLength = this.loadedTasksIds.length;
    const tasksIdsLength = this.tasksIds.length;

    if (loadedTasksIdsLength >= tasksIdsLength || this.isLoading) {
      return;
    }

    const tasksIdsCopy = structuredClone(this.tasksIds);
    const tasksIdsToLoad = tasksIdsCopy.splice(loadedTasksIdsLength, this.loadMoreTasksCount);
    this.lazyLoadTasks(tasksIdsToLoad, 'scroll');
  }

  /**
   * Load specific tasks without making a new request to getTasks.
   * Used to load new tasks with infinite scroll, or to refresh the status of a task already in list.
   *
   * @param tasksIds ids of the tasks
   * @param action 'scroll' or nothing
   */
  lazyLoadTasks(tasksIds: number[], action?: string) {
    if (action === 'scroll') {
      if (this.isLoadingMoreTasks) {
        return;
      }
      this.isLoadingMoreTasks = true;
    }

    const { parameters, sort_type_id, filters } = this.taskListConfiguration;
    const taskListConfiguration: TaskListConfiguration = {
      tasks_ids: tasksIds,
      parameters,
      sort_type_id,
      filters,
    };

    this.taskListService
      .lazyLoadTasks(taskListConfiguration)
      .pipe(
        finalize(() => {
          if (action === 'scroll') {
            this.isLoadingMoreTasks = false;
          }
        })
      )
      .subscribe({
        next: (result: TaskList) => {
          result.tasks_ids.forEach(id => {
            // backup "isChecked" before overwrite task
            result.tasks_by_id[id].isChecked = this.tasksById[id]?.isChecked;
            this.tasksById[id] = result.tasks_by_id[id];
          });

          if (action === 'scroll') {
            // Scroll case is when tasks are requested through a scroll action
            // If action is scroll we concatenate the new tasks ids to the loaded tasks ids as they already sorted
            this.loadedTasksIds = this.loadedTasksIds.concat(tasksIds);
          }
        },
      });
  }

  notifyNewTask(taskEvent: WebSocketTaskNotification) {
    const isMatching = this.countersService.isTaskMatchTaskListParams(
      taskEvent,
      this.taskListConfiguration.parameters,
      this.taskListConfiguration.filters
    );

    // test on .has() is necessary to not reopen the notif for the same task, if user has closed it
    // (for ex : if someone else change a tag on the task)
    if (isMatching && !this.newTasksIds.has(taskEvent.task_id) && !this.tasksById[taskEvent.task_id]) {
      this.newTasksIds.add(taskEvent.task_id);
      this.hasNewTasksIds = true;
    }

    if (!isMatching) {
      this.newTasksIds.delete(taskEvent.task_id);

      if (this.newTasksIds.size === 0) {
        this.hasNewTasksIds = false;
      }
    }

    this.makeTitleBlink();
  }

  reloadTaskList(allChannels: boolean) {
    // we could do this.$state.reload(), but it have a 1+ sec unexpected latency

    if (this.taskListMode === TaskListModeEnum.Inbox) {
      this.$state.go(
        this.stateInboxNgTasks,
        {
          queueId: this.taskListConfiguration.queueId,
          channelQueueId: allChannels ? 'all' : this.taskListConfiguration.channelQueueId,
        },
        {
          reload: this.stateInboxNgTasks,
        }
      );
    } else if (this.taskListMode === TaskListModeEnum.Pushbox) {
      this.$state.go(
        this.statePushboxNgTasks,
        {
          queueId: this.taskListConfiguration.queueId,
        },
        {
          reload: this.statePushboxNgTasks,
        }
      );
    }
  }

  closeNotification() {
    this.hasNewTasksIds = false;
    this.makeTitleBlink();
  }

  /**
   * Make the title blink if we have new messages notification, and if the browser active tab is not Alcméon.
   */
  makeTitleBlink() {
    const setTitleFn = (title: string) => {
      this.document.querySelector('title').innerText = title;
    };

    clearInterval(this.titleBlinkInterval);
    let isTitleBlinking = false;

    if (this.hasNewTasksIds) {
      this.titleBlinkInterval = setInterval(() => {
        if (!isTitleBlinking && this.document.visibilityState === 'hidden' && !this.$state.includes('**.process')) {
          setTitleFn(`📩🔴 ${this.currentTitle}`);
          isTitleBlinking = true;
        } else {
          setTitleFn(this.currentTitle);
          isTitleBlinking = false;
        }
      }, 1000);
    } else {
      setTitleFn(this.currentTitle);
    }
  }

  goToReceived(event: Event) {
    if ((event.target as any).nodeName === 'A') {
      this.$state.go(
        this.stateInboxNgTasks,
        {
          queueId: QueueIdEnum.Received,
        },
        {
          reload: this.stateInboxNg,
        }
      );
    }
  }

  resetFilters(event: Event) {
    if ((event.target as any).nodeName === 'A') {
      this.filtersService.resetfilters();
      this.navigationService.setSelectedSiteIdsAndUpdateAvailableTaskListConfigs([], []);
      this.reloadTaskList(true);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    clearInterval(this.titleBlinkInterval);
  }
}
